import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusBadge } from "@/components/service-order/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICE_ORDER_STATUSES } from "@/lib/constants";
import { Search, Plus, ClipboardList, Filter } from "lucide-react";

export default function ServiceOrders() {
  const { isSuperAdmin, activeTenantId, branches } = useTenant();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterBranch, setFilterBranch] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  const tenantParam = isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {};
  const canQuery = !isSuperAdmin || !!activeTenantId;

  async function fetchList() {
    if (!canQuery) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = { ...tenantParam };
      if (search) params.search = search;
      if (filterBranch !== "all") params.branch_id = filterBranch;
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      const { data } = await api.get("/service-orders", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(fetchList, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterBranch, filterStatus, filterDateFrom, filterDateTo, activeTenantId, isSuperAdmin]);

  return (
    <div className="space-y-6" data-testid="page-service-orders">
      <PageHeader
        title="Órdenes de servicio"
        description="Folio maestro de cada unidad que ingresa al taller. Aquí se conectan diagnóstico, órdenes de trabajo, evidencias y bitácora."
        actions={
          <Button
            className="btn-tenant"
            onClick={() => navigate("/ordenes-de-servicio/nueva")}
            disabled={!canQuery}
            data-testid="btn-new-service-order"
          >
            <Plus className="h-4 w-4 mr-2" /> Nueva orden de ingreso
          </Button>
        }
      />

      {/* Filters */}
      <div className="bg-white border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          <Filter className="h-3.5 w-3.5" /> Filtros
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Folio, cliente, vehículo, placas…"
              className="pl-9 h-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="so-search"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="so-filter-status"><SelectValue placeholder="Estatus" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estatus</SelectItem>
              {SERVICE_ORDER_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterBranch} onValueChange={setFilterBranch}>
            <SelectTrigger data-testid="so-filter-branch"><SelectValue placeholder="Sucursal" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sucursal activa</SelectItem>
              {branches.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              data-testid="so-filter-date-from"
              placeholder="Desde"
            />
            <Input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              data-testid="so-filter-date-to"
              placeholder="Hasta"
            />
          </div>
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10" />)}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={canQuery ? "Sin órdenes de servicio" : "Selecciona un tenant"}
            description={
              canQuery
                ? "Crea una orden de ingreso para empezar a documentar unidades del taller."
                : "Como Super Admin VRIntegrales, primero elige un tenant desde Tenants."
            }
            icon={ClipboardList}
            action={
              canQuery && (
                <Button
                  className="btn-tenant"
                  onClick={() => navigate("/ordenes-de-servicio/nueva")}
                  data-testid="empty-new-so"
                >
                  <Plus className="h-4 w-4 mr-2" /> Nueva orden de ingreso
                </Button>
              )
            }
          />
        ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Folio</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Vehículo</TableHead>
                <TableHead>Placas</TableHead>
                <TableHead>Sucursal</TableHead>
                <TableHead>Fecha ingreso</TableHead>
                <TableHead>Estatus</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((s) => (
                <TableRow
                  key={s.id}
                  className="tbl-row cursor-pointer"
                  onClick={() => navigate(`/ordenes-de-servicio/${s.id}`)}
                  data-testid={`row-so-${s.id}`}
                >
                  <TableCell className="mono font-medium text-tenant">{s.folio}</TableCell>
                  <TableCell className="text-sm">{s.customer_name || "—"}</TableCell>
                  <TableCell className="text-sm">{s.vehicle_summary || "—"}</TableCell>
                  <TableCell className="mono text-xs">{s.vehicle_plates || "—"}</TableCell>
                  <TableCell className="text-sm">{s.branch_name || "—"}</TableCell>
                  <TableCell className="text-sm">
                    {s.intake_date} <span className="text-muted-foreground">{s.intake_time || ""}</span>
                  </TableCell>
                  <TableCell><StatusBadge status={s.status} data-testid={`row-so-status-${s.id}`} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
